import React, {useEffect, useMemo, useRef, useState} from "react";
import Dialog from "../../../../../../components/base/dialog";
import {Col, Row} from "reactstrap";
import {DialogContent, DialogTitle, SvgIcon} from "@mui/material";
import {KeyboardArrowDown} from "@mui/icons-material";
import Button from "../../../../../../components/base/button/button";
import {OutlinedInputForm} from "../../../../../../components/base/input/outlined_input";
import Form from "../../../../../../components/base/form";
import IconButton from "../../../../../../components/base/button/icon_button";
import {ReactComponent as Copy} from '../../../../../../../assets/images/copy.svg'
import ValidateMessages from "../../../../../../../core/models/static/texts/vallidate-messages";
import {CheckboxForm} from "../../../../../../components/base/checkbox/checkbox";
import Api from "../../../../../../../core/services/api/api";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import CopyToClipboard from "../../../../../../components/base/copy-to-clipboard";
import {deepEqual} from "../../../../../../../core/services/utils/utils";
import debounce from "lodash/debounce";
import LoadingLottieAnimation from "../../../../../../components/app-specific/lottie-animations/loading";
import DateUtils from "../../../../../../../core/services/date-utils";
import {MuiRFF, Yup} from '../../../../../../components/form';
import {useOperatorInformation} from "../../../../../../hooks/use-operator-information";


const schema = Yup.object().shape({
    title: Yup.string().trim().nullable(),
    expireDate: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .disablePastDate(ValidateMessages.minDate("today"), 'day')
        .nullable(),
    clickLimitCount: Yup.number()
        .min(1, ValidateMessages.greaterThan('0'))
        .typeError(ValidateMessages.incorrectType('number'))
        .nullable(),
    expireDateCheckbox: Yup.boolean().default(false),
    clickLimitCountCheckbox: Yup.boolean().default(false),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const ManagePartShareLinkDialog = ({open, setOpen, partId}) => {
    const [link, setLink] = useState(undefined);
    const [advancedOpen, setAdvanceOpen] = useState(false)
    const [generatingLink, setGeneratingLink] = useState(false);
    const [updatingLink, setUpdatingLink] = useState(false)
    const isMounted = useIsMounted();
    const linkRef = useRef(link);
    const operatorInformation = useOperatorInformation();

    const initialValues = useMemo(() => ({
        title: link?.title ?? undefined,
        expireDate: link?.expireDate ? DateUtils.serverToUTC(link.expireDate) : null,
        clickLimitCount: link?.clickLimitCount?.toString() ?? undefined,
        expireDateCheckbox: !!link?.expireDate,
        clickLimitCountCheckbox: !!link?.clickLimitCount || link?.clickLimitCount === 0
    }), [link])

    /**@type {boolean}*/
    const disabled = useMemo(() => updatingLink || generatingLink, [updatingLink, generatingLink])
    /**@type {boolean}*/
    const generatingForFirstTime = useMemo(() => generatingLink && !link, [generatingLink, link]);

    /**
     * Watches the open and link prop changes and for each change:
     * if not open then close advanced, and if opened,
     * compare the state link and prop link, if equal do nothing.
     * Set the link and updated link as the prop link
     */
    useEffect(() => {
        if (!open) {
            setUpdatingLink(false);
            setAdvanceOpen(false);
            return
        }
        if (!link) {
            generateShareLink().then();
        }
    }, [open])

    /**
     * Generates a new share link for the provided part.
     *
     * * if the response of the api call is successful, sets the generated link in the state, else, closes the dialog.
     */
    const generateShareLink = async () => {
        const forApi = {
            title: `${operatorInformation?.name ?? ""} @ ${DateUtils.localNow().format(DateUtils.dateTimeFormat)}`,
            partId: partId,
        }
        setGeneratingLink(true);
        const response = await Api.generateLink(forApi)
        if (!isMounted())
            return;
        setGeneratingLink(false);
        if (response?.resultFlag && !!response?.data) {
            linkRef.current = response.data;
            return setLink(response.data)
        }
        setOpen(false);
    }

    /**
     * Updates the current part share link values with the provided entries in the server.
     *
     * * only if the response of the api is successful, we update the link properties.
     */
    const updateShareLink = async (values) => {
        if (updatingLink || !link)
            return;
        const newLink = {
            id: link.id,
            title: values.title ?? undefined,
            partId: partId,
            clickLimitCount: values?.clickLimitCountCheckbox && !Number.isNaN(Number(values?.clickLimitCount))
                ? parseInt(values.clickLimitCount)
                : undefined,
            expireDate: values?.expireDateCheckbox && !!values.expireDate && values.expireDate.isValid()
                ? DateUtils.UTCToServer(values.expireDate).format(DateUtils.withoutOffsetFormat)
                : undefined
        }
        const currentLink = {
            id: linkRef.current?.id,
            title: linkRef.current?.title ?? undefined,
            partId: partId,
            clickLimitCount: linkRef.current?.clickLimitCount ?? undefined,
            expireDate: linkRef.current?.expireDate
                ? DateUtils.server(linkRef.current.expireDate).format(DateUtils.withoutOffsetFormat)
                : undefined,
        };
        if (deepEqual(newLink, currentLink)) {
            return;
        }
        setUpdatingLink(true);
        const response = await Api.updateLink(newLink);
        if (!isMounted())
            return;
        setUpdatingLink(false);
        if (response?.resultFlag) {
            setLink(prevState => {
                const newState = ({
                    ...(prevState ?? {}),
                    ...(response?.data ?? {}),
                })
                linkRef.current = newState;
                return newState;
            });
        }
    }

    /**@type {function(form: formApi): void}*/
    const submitForm = debounce((form) => form?.submit(), 2000);

    /**
     * Removes the expiry date if the value of the checkbox is set to false.
     * @param {ClickEvent} e
     * @param {formApi} form
     */
    const onTimeLimitationChanged = (e, form) => {
        form.change('expireDateCheckbox', e.target.checked);
        if (!e.target.checked) {
            form.change('expireDate', undefined);
        }
        form.resetFieldState('expireDate');
    }

    /**
     * Removes the click limit count if the value of the checkbox is set to false.
     * @param {ClickEvent} e
     * @param {formApi} form
     */
    const onClickLimitationChanged = (e, form) => {
        form.change('clickLimitCountCheckbox', e.target.checked);
        if (!e.target.checked) {
            form.change('clickLimitCount', undefined);
        }
        form.resetFieldState('clickLimitCount');
    }

    /**@type {JSX.Element}*/
    const form = useMemo(() => (
        <Form
            className='px-4 mt-3 w-100'
            onSubmit={updateShareLink}
            validate={validate}
            initialValues={initialValues}
            render={({values, form}) => {
                submitForm(form);
                return (
                    <Row className='no-gutters justify-content-center w-100'>
                        <Col xs={12}
                             className='mb-4'>
                            <OutlinedInputForm
                                label={'Title (optional)'}

                                name={'title'}
                                type={'text'}
                                required={required.title}
                                disabled={disabled}
                            />
                        </Col>
                        <Col xs={12} className=' mb-4'>
                            <p className='text-xs text-color-1 mb-2'>
                                Shareable Link:
                            </p>
                            <Row
                                className={`background-secondary-color-3 rounded-xl no-gutters align-items-center justify-content-between px-3 py-2`}>
                                <p className='text-color-1 text-sm w-75'>
                                    {link?.shareLink ?? ''}
                                </p>
                                {
                                    link?.shareLink &&
                                    <CopyToClipboard>
                                        {({copy}) =>
                                            <IconButton
                                                onPressed={() => copy(link?.shareLink ?? '')}

                                                color={'secondary'}
                                                className={'copy-to-clipboard'}
                                            >
                                                <SvgIcon className='icon normal' component={Copy}/>
                                            </IconButton>
                                        }
                                    </CopyToClipboard>
                                }
                            </Row>
                        </Col>
                        {
                            !advancedOpen
                                ?
                                <Row className='w-100 justify-content-center align-items-center mb-2 w-100'>
                                    <Button
                                        className='px-3 py-2 d-flex justify-content-center align-items-center'
                                        variant={'text'}
                                        color={'primary'}
                                        onClick={() => setAdvanceOpen(true)}
                                        disabled={disabled}
                                    >
                                        <p>Advance</p>
                                        <KeyboardArrowDown/>
                                    </Button>
                                </Row>
                                : <Col xs={12}
                                       className='mb-4 mt-1 row no-gutters justify-content-between align-items-center w-100'>
                                    <Row className='no-gutters justify-content-between mb-4 w-100'>
                                        <Col xs={5}>
                                            <CheckboxForm
                                                name={'clickLimitCountCheckbox'}
                                                required={required.clickLimitCountCheckbox}
                                                disabled={disabled}
                                                data={{
                                                    value: values.clickLimitCountCheckbox,
                                                    label: 'Click Limitation',
                                                    disabled: disabled,
                                                }}
                                                onChange={(e) => onClickLimitationChanged(e, form)}
                                            />
                                        </Col>
                                        <Col xs={7}>
                                            <OutlinedInputForm
                                                disabled={!values?.clickLimitCountCheckbox || disabled}
                                                label={'Click Limitation'}
                                                name={'clickLimitCount'}
                                                type={'number'}
                                                required={required.clickLimitCount}
                                            />
                                        </Col>
                                    </Row>
                                    <Row className='no-gutters justify-content-between w-100'>
                                        <Col xs={5}>
                                            <CheckboxForm
                                                name={'expireDateCheckbox'}
                                                required={required.expireDateCheckbox}
                                                disabled={disabled}
                                                data={{
                                                    value: values.expireDateCheckbox,
                                                    label: 'Time Limitation',
                                                    disabled: disabled,
                                                }}
                                                onChange={(e) => onTimeLimitationChanged(e, form)}
                                            />
                                        </Col>
                                        <Col xs={7}>
                                            <MuiRFF.DatePicker
                                                name={'expireDate'}
                                                label={"Time Limitation"}
                                                required={required.expireDate}
                                                disabled={!values?.expireDateCheckbox || disabled}
                                                disablePast
                                            />
                                        </Col>
                                    </Row>
                                </Col>
                        }
                    </Row>
                );
            }}
        />
    ), [disabled, advancedOpen, link?.shareLink, initialValues])

    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            maxWidth={'sm'}
        >
            <DialogTitle>
                <div className={'d-flex w-100 align-items-center justify-content-between'}>
                    <p className={`text-lg text-color-1 font-weight-semi-bold`}>
                        Manage Part's Share Link
                    </p>
                    {
                        link &&
                        <Button
                            variant={'text'}
                            color={'primary'}
                            className={'py-1 px-2'}
                            disabled={generatingLink}
                            onClick={generateShareLink}
                        >
                            {
                                generatingLink
                                    ? "Generating..."
                                    : "New Link"
                            }
                        </Button>
                    }
                </div>
            </DialogTitle>
            <DialogContent dividers>
                {
                    generatingForFirstTime &&
                    <>
                        <div className='my-2 w-100'/>
                        <LoadingLottieAnimation width={'80%'}/>
                        <div className='my-2 w-100'/>
                    </>
                }
                {
                    !generatingForFirstTime &&
                    form
                }
            </DialogContent>
        </Dialog>
    );
}

export default ManagePartShareLinkDialog;
